import classNames from "classnames";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { handlingDynamicFontBasedOnLanguage } from "../../../utils/UtilsTypography";
import "./Typography.scss";

/**
 * Enum representing the available typography variants.
 *
 * @description Defines the text style variants for the Typography component.
 * Each variant corresponds to a specific visual style defined in the CSS.
 *
 * @property {string} h1 - Heading 1, largest heading style
 * @property {string} h2 - Heading 2, second largest heading style
 * @property {string} h3 - Heading 3, third largest heading style
 * @property {string} title1 - Title 1 style
 * @property {string} title2 - Title 2 style
 * @property {string} title3 - Title 3 style
 * @property {string} body1 - Body text style 1
 * @property {string} body2 - Body text style 2
 * @property {string} body3 - Body text style 3
 * @property {string} caption - Caption text style, smallest text
 * @property {string} flag - Flag text style
 */
enum variants {
  h1,
  h2,
  h3,
  title1,
  title2,
  title3,
  body1,
  body2,
  body3,
  caption,
  flag,
}

/**
 * Enum representing the available font weights.
 *
 * @description Defines the font weight options for the Typography component.
 *
 * @property {string} w700 - Font weight 700 (Bold)
 * @property {string} w400 - Font weight 400 (Normal)
 * @property {string} w500 - Font weight 500 (Medium)
 * @property {string} wMedium - Medium font weight alias
 * @property {string} wBold - Bold font weight alias
 */
enum weights {
  w700,
  w400,
  w500,
  wMedium,
  wBold,
}

/**
 * Props for the Typography component.
 *
 * @description The Typography component is a text rendering component that provides
 * consistent typography styles across the application with support for multiple
 * variants, weights, and dynamic font handling based on language.
 *
 * @property {keyof typeof variants} variant - The typography style variant. Required.
 *   Available options: "h1", "h2", "h3", "title1", "title2", "title3",
 *   "body1", "body2", "body3", "caption", "flag"
 * @property {keyof typeof weights} [weight] - The font weight.
 *   Available options: "w700", "w400", "w500", "wMedium", "wBold"
 * @property {string} [component="div"] - The HTML element to render as.
 *   Common values: "div", "span", "p", "h1", "h2", "h3", "label"
 * @property {React.ReactNode} [children] - The text content to render.
 * @property {string} [className] - Additional CSS class names.
 * @property {React.CSSProperties} [style] - Inline styles to apply.
 * @property {() => void} [onClick] - Click event handler.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic typography
 * <Typography variant="h1">Main Heading</Typography>
 *
 * @example
 * // Body text with custom element
 * <Typography variant="body1" component="p">
 *   This is a paragraph of text.
 * </Typography>
 *
 * @example
 * // Title with weight
 * <Typography variant="title1" weight="w700">
 *   Bold Title
 * </Typography>
 *
 * @example
 * // Clickable text
 * <Typography
 *   variant="body2"
 *   component="span"
 *   onClick={handleClick}
 *   className="clickable-text"
 * >
 *   Click me
 * </Typography>
 */
interface Props {
  variant: keyof typeof variants;
  weight?: keyof typeof weights;
  component?: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  testId?: string;
}

/**
 * A typography component for consistent text styling.
 *
 * @description Typography renders text with consistent styling based on the
 * selected variant. It supports dynamic font handling based on the current
 * language and provides flexibility in the HTML element used for rendering.
 *
 * Features:
 * - Multiple typography variants (h1-h3, title1-3, body1-3, caption, flag)
 * - Configurable font weights
 * - Custom HTML element rendering
 * - Dynamic font class based on language
 * - Click event support
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A text element with the configured variant and styling
 *
 * @see Props for all available properties
 * @see variants for available typography variants
 * @see weights for available font weights
 */
export const Typography: React.FC<Props> = (props) => {
  const {
    variant,
    children,
    component = "div",
    className,
    style,
    weight = "",
    onClick,
    testId,
  } = props;

  // Get lang attribute from document
  const currentLang =
    typeof document === "undefined" ? "" : document.documentElement.lang;

  // Get i18n instance for Trans component support
  const { i18n } = useTranslation();

  const fontClass = useMemo(
    () => handlingDynamicFontBasedOnLanguage(currentLang, children, i18n),
    [currentLang, children, i18n]
  );

  const wrapClass = classNames(
    "typography",
    { [variant]: !!variant },
    { [weight]: !!weight },
    fontClass,
    className
  );

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const CustomTag = component as keyof JSX.IntrinsicElements;

  return (
    <CustomTag
      tabIndex={-1}
      data-testid={testId}
      className={wrapClass}
      style={style}
      onClick={handleOnClick}
    >
      {children}
    </CustomTag>
  );
};
